/**
 * @name Snackbar
 * @description Componente común de notificaciones snackbar (success, error, info, warning)
 * @author Ricardo Guerra
 */

import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import styled, { css, keyframes } from 'styled-components';

import { Icons, MUI, Typography, Unicons } from '@styles';

// Material-UI
import MuiSnackbarContent from '@material-ui/core/SnackbarContent';

const DEFAULT_AUTO_CLOSE_MS = 4600;

const Snackbar = ({ id, message, variant, autoClose, autoCloseMs, removeSnack }) => {
  const closeTimeoutRef = useRef(null);

  const typeIcon = (type) => {
    switch (type) {
      case 'success':
        return <Unicons.CircleCheck size="20" />;
      case 'info':
        return <Unicons.CircleInfo size="20" />;
      case 'error':
        return <Unicons.CircleTimes size="20" />;
      case 'warn':
        return <Unicons.OctagonExclamation size="20" />;
      default:
        return null;
    }
  };

  useEffect(() => {
    if (!autoClose) {
      return undefined;
    }

    closeTimeoutRef.current = setTimeout(() => {
      if (typeof removeSnack === 'function') {
        removeSnack(id);
      }
    }, autoCloseMs);

    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  }, [autoClose, autoCloseMs, id, removeSnack]);

  return (
    <Styled.SnackbarContent
      color={variant}
      autoClose={autoClose}
      message={
        <>
          <Styled.BoxIcon>
            {typeIcon(variant)}
          </Styled.BoxIcon>
          <Styled.MessageText overflow={3} color="white">
            {message}
          </Styled.MessageText>
        </>
      }
      autoCloseMs={autoCloseMs}
      action={
        <Styled.BoxClose color="default" onClick={() => removeSnack(id)}>
          <Icons.Close />
        </Styled.BoxClose>
      }
    />
  );
};

Snackbar.propTypes = {
  // Id del componente snackbar.
  id: PropTypes.string.isRequired,
  // Mensaje del componente snackbar.
  message: PropTypes.string.isRequired,
  // Tipo de componente snackbar.
  variant: PropTypes.string.isRequired,
  // Boolean que indica si el snackbar debe autocerrarse.
  autoClose: PropTypes.bool,
  // Duración del autocierre en milisegundos.
  autoCloseMs: PropTypes.number,
  // Función para eliminar snackbar.
  removeSnack: PropTypes.func
};

Snackbar.defaultProps = {
  autoClose: false,
  autoCloseMs: DEFAULT_AUTO_CLOSE_MS,
};

// *************************************
// STYLED COMPONENTS
// *************************************

const snackbarColor = {
  success: (props) => props.theme.colors.green,
  error: (props) => props.theme.colors.red,
  info: (props) => props.theme.colors.blueMedium,
  warn: (props) => props.theme.colors.orange,
};

const countdown = keyframes`
  from {
    width: 100%;
  }
  to {
    width: 0%;
  }
`;

const Styled = {};

Styled.BoxIcon = styled.div`
  display: flex;
  margin-right: 12px;
  color: ${(props) => props.theme.colors.white};

  .MuiSvgIcon-root {
    font-size: 20px;
  }
`;

Styled.BoxClose = styled(MUI.ButtonIcon)`
  &.MuiIconButton-root,
  &.MuiIconButton-colorPrimary,
  &.MuiIconButton-colorSecondary {
    padding: 4px;
    margin: 0 0 0 8px;
    color: ${(props) => props.theme.colors.white};
  }

  &.MuiIconButton-root:hover,
  &.MuiIconButton-colorPrimary:hover,
  &.MuiIconButton-colorSecondary:hover {
    background-color: rgba(255, 255, 255, 0.14);
    color: ${(props) => props.theme.colors.white};
  }

  .MuiSvgIcon-root {
    font-size: 14px;
  }
`;

Styled.MessageText = styled(Typography.Text)`
  flex: 1;
`;

// SnackbarContent
Styled.SnackbarContent = styled(MuiSnackbarContent)`
  &.MuiSnackbarContent-root {
    position: relative;
    min-height: 80px;
    padding: 12px;
    margin: 12px 0px;
    border-radius: 10px;
    background-color: ${(props) => snackbarColor[props.color]};
    box-shadow: ${(props) => props.theme.shadows.depth2};
    overflow: hidden;
  }

  @media screen and (min-width: 600px) {
    &.MuiSnackbarContent-root {
      width: 360px;
    }
  }

  .MuiSnackbarContent-message {
    display: flex;
    align-items: center;
    padding: 0px;
    width: calc(100% - 44px);
    overflow: hidden;
  }

  .MuiSnackbarContent-action {
    display: flex;
    align-items: flex-start;
    padding: 0px;
    margin-right: 0px;
  } 

  ${(props) => props.autoClose && css`
    &::after {
      content: '';
      position: absolute;
      left: 0;
      bottom: 0;
      height: 3px;
      background-color: rgba(255, 255, 255, 0.60);
      animation: ${countdown} ${(props) => props.autoCloseMs}ms linear forwards;
    }
  `}
`;

export default Snackbar;